import { useCallback, useEffect, useMemo, useState } from 'react';

/** Long enough that a word typed at speed is one request, short enough to
 *  feel immediate. The phone's search waits the same. */
export const SEARCH_DEBOUNCE_MS = 300;

/** The condition the toolbar search stands for: the movement's own text. */
export function noteCondition(text) {
  return { entity: 'Movement', column: 'note', operator: 'ilike', values: [text] };
}

/**
 * MI-326 — the Movements toolbar search, applied as you type.
 *
 * It used to narrow only the rows already on screen, and only in the ungrouped
 * list: under a grouping — Day is the default — typing did nothing until Enter
 * turned the text into a filter chip. Now, after a short pause, the text joins
 * the page's filters as a note condition, so every query the page makes (group
 * headers, row windows, the ungrouped list, totals) narrows with it, the way
 * the phone's search already does.
 *
 * Emptying the field drops the condition at once rather than after the pause:
 * waiting to show everything again only looks slow. `clear` does the same for
 * Enter, which pins the text as a chip and empties the field in one step.
 */
export default function useNoteSearch(filters) {
  const [searchText, setSearchText] = useState('');
  const [applied, setApplied] = useState('');

  useEffect(() => {
    const text = searchText.trim();
    if (!text) {
      setApplied('');
      return undefined;
    }
    const timer = setTimeout(() => setApplied(text), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  const queryFilters = useMemo(
    () => (applied ? [...filters, noteCondition(applied)] : filters),
    [filters, applied],
  );

  const clear = useCallback(() => {
    setSearchText('');
    setApplied('');
  }, []);

  return { searchText, setSearchText, queryFilters, clear };
}
